import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from '@/hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Trial warning check interval (every 5 minutes)
const WARNING_CHECK_INTERVAL = 5 * 60 * 1000;

// Tier features for client-side checks
const TIER_FEATURES = {
  free: {
    frequencies: 'national',
    saved_locations: 1,
    ai_chat: 10,
    cameras: 5,
    scanners: 3,
    voice_alerts: false,
    real_time_alerts: false,
    export_frequencies: false,
    family_sharing: 0,
    ads: true,
    incident_map: 'basic',
    threat_analysis: false,
    daily_briefing: false,
    watch_zones: 1,
    sex_offender_registry: false,
    weather_alerts: true,
    terrorism_alerts: false,
  },
  basic: {
    frequencies: 'all',
    saved_locations: -1,
    ai_chat: 50,
    cameras: -1,
    scanners: -1,
    voice_alerts: true,
    real_time_alerts: true,
    export_frequencies: true,
    family_sharing: 0,
    ads: false,
    incident_map: 'full',
    threat_analysis: true,
    daily_briefing: true,
    watch_zones: 3,
    sex_offender_registry: true,
    weather_alerts: true,
    terrorism_alerts: true,
  },
  pro: {
    frequencies: 'all',
    saved_locations: -1,
    ai_chat: -1,
    cameras: -1,
    scanners: -1,
    voice_alerts: true,
    real_time_alerts: true,
    export_frequencies: true,
    family_sharing: 2,
    ads: false,
    incident_map: 'full',
    threat_analysis: true,
    daily_briefing: true,
    watch_zones: 10,
    priority_alerts: true,
    sex_offender_registry: true,
    weather_alerts: true,
    terrorism_alerts: true,
  },
  business: {
    frequencies: 'all',
    saved_locations: -1,
    ai_chat: -1,
    cameras: -1,
    scanners: -1,
    voice_alerts: true,
    real_time_alerts: true,
    export_frequencies: true,
    family_sharing: 5,
    ads: false,
    incident_map: 'full',
    threat_analysis: true,
    daily_briefing: true,
    watch_zones: -1,
    priority_alerts: true,
    team_members: 5,
    api_access: true,
    data_export: true,
    sex_offender_registry: true,
    weather_alerts: true,
    terrorism_alerts: true,
  },
  lifetime: {
    frequencies: 'all',
    saved_locations: -1,
    ai_chat: -1,
    cameras: -1,
    scanners: -1,
    voice_alerts: true,
    real_time_alerts: true,
    export_frequencies: true,
    family_sharing: 5,
    ads: false,
    incident_map: 'full',
    threat_analysis: true,
    daily_briefing: true,
    watch_zones: -1,
    priority_alerts: true,
    team_members: 5,
    api_access: true,
    data_export: true,
    founder_badge: true,
    early_access: true,
    dev_access: true,
    sex_offender_registry: true,
    weather_alerts: true,
    terrorism_alerts: true,
  }
};

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { token, user } = useAuth();
  const { toast } = useToast();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState('free');
  const [features, setFeatures] = useState(TIER_FEATURES.free);
  
  // Trial state
  const [trial, setTrial] = useState(null);
  const [trialActive, setTrialActive] = useState(false);
  const [trialTimeRemaining, setTrialTimeRemaining] = useState(null);
  const [trialWarning, setTrialWarning] = useState(null);
  const trialTimerRef = useRef(null);
  const warningCheckRef = useRef(null);
  
  // Check for trial warnings
  const checkTrialWarning = useCallback(async () => {
    if (!token || !trialActive) return;
    
    try {
      const res = await fetch(`${API_URL}/api/notifications/trial-warning`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        
        if (data.warning && data.should_notify) {
          setTrialWarning(data);
          
          // Show toast notification
          toast({
            title: data.title,
            description: data.tank_says,
            variant: data.urgency === 'critical' ? 'destructive' : 'default',
            duration: data.urgency === 'critical' ? 10000 : 5000,
          });
        }
      }
    } catch (err) {
      console.error('Failed to check trial warning:', err);
    }
  }, [token, trialActive, toast]);
  
  // Fetch trial status
  const fetchTrialStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/trial-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTrial(data);
        setTrialActive(data.trial_active || false);
        
        if (data.trial_active && data.seconds_remaining) {
          setTrialTimeRemaining(data.seconds_remaining);
        }
      }
    } catch (err) {
      console.error('Failed to fetch trial status:', err);
    }
  }, [token]);
  
  // Trial countdown timer
  useEffect(() => {
    if (trialActive && trialTimeRemaining > 0) {
      trialTimerRef.current = setInterval(() => {
        setTrialTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(trialTimerRef.current);
            setTrialActive(false);
            fetchSubscription(); // Refresh to get updated status
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(trialTimerRef.current);
    }
  }, [trialActive, trialTimeRemaining]);
  
  // Trial warning check interval
  useEffect(() => {
    if (trialActive) {
      // Check immediately on trial start
      checkTrialWarning();
      
      // Then check periodically (every 5 minutes)
      warningCheckRef.current = setInterval(checkTrialWarning, WARNING_CHECK_INTERVAL);
      
      return () => {
        if (warningCheckRef.current) {
          clearInterval(warningCheckRef.current);
        }
      };
    }
  }, [trialActive, checkTrialWarning]);
  
  // Fetch subscription status
  const fetchSubscription = useCallback(async () => {
    if (!token) {
      setTier('free');
      setFeatures(TIER_FEATURES.free);
      setSubscription(null);
      setLoading(false);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/my-subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSubscription(data);
        setTier(data.tier || 'free');
        setFeatures(TIER_FEATURES[data.tier] || TIER_FEATURES.free);
      } else {
        setTier('free');
        setFeatures(TIER_FEATURES.free);
      }
      
      // Also fetch trial status
      await fetchTrialStatus();
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setTier('free');
      setFeatures(TIER_FEATURES.free);
    } finally {
      setLoading(false);
    }
  }, [token, fetchTrialStatus]);
  
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);
  
  // Start trial
  const startTrial = useCallback(async () => {
    if (!token) return { success: false, message: 'Please log in first' };
    
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/start-trial`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // Update local state
        setTrialActive(true);
        setTrial(data.trial);
        setTrialTimeRemaining(24 * 60 * 60); // 24 hours in seconds
        setFeatures(TIER_FEATURES.pro); // Grant Pro features during trial
        return { success: true, data };
      } else {
        return { success: false, message: data.detail || 'Failed to start trial' };
      }
    } catch (err) {
      console.error('Failed to start trial:', err);
      return { success: false, message: 'Network error. Please try again.' };
    }
  }, [token]);
  
  // Check if user has access to a feature (considers trial)
  const hasFeature = useCallback((featureId) => {
    // If trial is active, grant Pro features
    if (trialActive) {
      const value = TIER_FEATURES.pro[featureId];
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value !== 0;
      if (typeof value === 'string') return true;
      return false;
    }
    
    const value = features[featureId];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') return true;
    return false;
  }, [features, trialActive]);
  
  // Check frequency access level (considers trial)
  const getFrequencyAccess = useCallback(() => {
    if (trialActive) return 'all';
    return features.frequencies || 'national';
  }, [features, trialActive]);
  
  // Check if user can access a specific state's frequencies
  const canAccessState = useCallback((stateCode) => {
    const access = getFrequencyAccess();
    if (access === 'all') return true;
    if (access === 'national') return stateCode === 'ALL';
    return false;
  }, [getFrequencyAccess]);
  
  // Get tier display info (considers trial)
  const getTierInfo = useCallback(() => {
    if (trialActive) {
      return { 
        name: 'Trial', 
        displayName: 'Tactical Preview', 
        color: 'orange',
        isTrial: true
      };
    }
    const tierNames = {
      free: { name: 'Scout', displayName: 'Free Scout', color: 'slate' },
      basic: { name: 'Basic', displayName: 'VeriWatch Basic', color: 'blue' },
      pro: { name: 'Pro', displayName: 'VeriWatch Pro', color: 'teal' },
      business: { name: 'Business', displayName: 'VeriWatch Business', color: 'purple' },
      lifetime: { name: 'Founder', displayName: 'Lifetime Founder', color: 'amber' }
    };
    return tierNames[tier] || tierNames.free;
  }, [tier, trialActive]);
  
  // Format trial time remaining
  const getTrialTimeFormatted = useCallback(() => {
    if (!trialTimeRemaining) return null;
    const hours = Math.floor(trialTimeRemaining / 3600);
    const minutes = Math.floor((trialTimeRemaining % 3600) / 60);
    const seconds = trialTimeRemaining % 60;
    return { hours, minutes, seconds, total: trialTimeRemaining };
  }, [trialTimeRemaining]);
  
  // Check if user is on paid tier or has active trial
  const isPaid = useCallback(() => {
    return trialActive || tier === 'pro' || tier === 'business' || tier === 'lifetime';
  }, [tier, trialActive]);
  
  // Check if user is a lifetime member
  const isLifetime = useCallback(() => {
    return tier === 'lifetime' || subscription?.is_lifetime;
  }, [tier, subscription]);
  
  // Check if trial is available
  const canStartTrial = useCallback(() => {
    return !trialActive && !trial?.trial_used && tier === 'free';
  }, [trialActive, trial, tier]);
  
  // Refresh subscription (call after upgrade/downgrade)
  const refreshSubscription = useCallback(() => {
    fetchSubscription();
  }, [fetchSubscription]);
  
  const value = {
    subscription,
    tier,
    features,
    loading,
    hasFeature,
    getFrequencyAccess,
    canAccessState,
    getTierInfo,
    isPaid,
    isLifetime,
    refreshSubscription,
    // Trial-specific
    trial,
    trialActive,
    trialTimeRemaining,
    trialWarning,
    startTrial,
    canStartTrial,
    getTrialTimeFormatted,
    checkTrialWarning,
  };
  
  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
